import { Injectable } from '@angular/core';
import { ICardInfo, ISmallCardData } from '../interfaces/stanalone-components.data';
import {
    IRowItems,
    SmallCardData,
    SmallCardDataFromDb,
} from '../../../../shared/interfaces/card-data';
import { NoDataActionType, NoDataSourceType } from '../../../../shared/interfaces/no-data';
import { ReusableFunctions } from '../../../../shared/classes/reusable-functions';
import { PipelineService } from '../../../../pipeline/services/pipeline.service';
import { DisplayOptions } from '../../../../shared/interfaces/display-options';
import { IPipelineParameters } from '../../../../pipeline/interfaces/pipeline-data';
import {
    ProductionSmallCard,
    ProductionSmallCardMilestoneAvg,
} from '../../../../production/interfaces/production';
import { ProductionService } from '../../../../production/services/production.service';
import { PerformanceService } from 'app/modules/performance/services/performance.service';
import {
    IHeadCountTotals,
    IScoreCardGrades,
} from 'app/modules/performance/interfaces/performance-data';
import { UserAccessService } from 'app/modules/shared/services/user-access.service';
import { PositionService } from 'app/modules/shared/services/position.service';

@Injectable({
    providedIn: 'root',
})
export class SmallCardService {
    private serverUrl = 'https://' + window.location.host; // TODO Get url and version number from appsettings.
    private version = '0.5';
    private componentName = 'pipeline';
    private _url = (this.serverUrl + '/api/v{version}/{componentName}')
        .replace('{version}', this.version)
        .replace('{componentName}', this.componentName);

    constructor(
        private reusableFunctions: ReusableFunctions,
        private _productionService: ProductionService,
        private _pipelineServices: PipelineService,
        private performanceService: PerformanceService,
        private userAccessService: UserAccessService,
        private positionService: PositionService
    ) {}

    //--------------- TODO: Minimize repetition code  --------------------------
    initializeSmallCard(cardInfo: ICardInfo, action: NoDataActionType) {
        return Object.assign(
            {},
            this.populatePipelineSmallCard(
                '',
                null,
                false,
                cardInfo.title,
                cardInfo.isInverse,
                '',
                cardInfo.cardId,
                false,
                false,
                '',
                action
            )
        );
    }

    populatePipelineSmallCard(
        mainVal: number | string,
        comparisonVal: number,
        isComparable: boolean,
        title: string,
        isInverse: boolean,
        units: string,
        urlSegment: string,
        isDataReady: boolean,
        hasData: boolean,
        routerLink: string,
        action: NoDataActionType
    ): SmallCardData {
        if (comparisonVal == null) {
            return {
                title: title,
                mainValue: mainVal.toString(),
                unit: units,
                faIcon: null,
                faIconColor: null,
                comparisonValue: null,
                urlSegment: urlSegment,
                routerLink: routerLink,
                svgData: null,
                isDataReady: isDataReady,
                hasData: hasData,
                searchTerm: '',
                action: action,
                noDataSource: NoDataSourceType.verySmallDataCard,
            };
        }

        const compareVal = this.reusableFunctions.getCaret(
            comparisonVal,
            isInverse,
            null,
            isComparable
        );
        return {
            title: title,
            mainValue: mainVal.toString(),
            unit: units,
            faIcon: compareVal.ng_caret_icon,
            faIconColor: compareVal.color,
            comparisonValue: compareVal.comparison,
            urlSegment: urlSegment,
            routerLink: routerLink,
            svgData: compareVal.svgData,
            isDataReady: isDataReady,
            hasData: hasData,
            searchTerm: '',
            action: action,
            noDataSource: NoDataSourceType.verySmallDataCard,
        };
    }

    extractValues(data: SmallCardDataFromDb[], milestoneKey: number): Array<SmallCardDataFromDb> {
        const dataPerMilestone = data.filter((obj) => obj.milestoneKey === milestoneKey);
        return [
            dataPerMilestone.find((obj) => obj.periodGroup === 'This Period'),
            dataPerMilestone.find((obj) => obj.periodGroup === 'Compare Period'),
        ];
    }

    setPipelineSmallCardData(
        data: any[],
        cardInf: ICardInfo,
        action: NoDataActionType,
        routerLink: string
    ) {
        let hasData = data.length > 0;
        let comparisonPercentage;
        let isComparable = false;
        let mainValue = '0';

        if (cardInf.cardId === 'expiring') {
            mainValue = hasData ? data[0][cardInf.property_1] : 0;
            comparisonPercentage = null;
        } else {
            let extractedData = this.extractValues(data, cardInf.milestoneKey);
            hasData = data.length > 1;
            let thisPeriod = extractedData[0];
            let lastPeriod = extractedData[1];
            let currentValue = hasData ? thisPeriod[cardInf.property_1] : 0;
            let previousValue = hasData ? lastPeriod[cardInf.property_1] : 0;
            mainValue =
                cardInf.dataType === 'money'
                    ? this.reusableFunctions.formatMoney(currentValue)
                    : Math.round(currentValue).toLocaleString();
            let comparisonInfo = this.reusableFunctions.calculateComparisonPercentage(
                currentValue,
                previousValue
            );
            comparisonPercentage = comparisonInfo.percentage;
            isComparable = comparisonInfo.isComparable;
        }
        cardInf.card = this.populatePipelineSmallCard(
            mainValue,
            comparisonPercentage,
            isComparable,
            cardInf.title,
            cardInf.isInverse ?? false,
            '',
            cardInf.cardId,
            true,
            hasData,
            routerLink,
            action
        );
        cardInf.card.showCardControllers = true;
        cardInf.card.action = action;
    }

    getPipelineSmallCardData(
        displayOptions: DisplayOptions[],
        hFilterJSON: string,
        cardInfo: ICardInfo,
        action: NoDataActionType,
        routerLink: string
    ) {
        if (displayOptions) {
            let fromDatePeriod = displayOptions.find(
                (item) => item.optionKey === 'dateRange_y_q_m'
            ).selectedOptions;
            let priorCompareToInfo = displayOptions.find((item) => item.optionKey === 'compareTo');
            let priorCompareTo = priorCompareToInfo.selectedOptions;

            let channel = displayOptions.find((x) => x.optionKey === 'channel').selectedOptions;
            let loanType = displayOptions.find(
                (x) => x.optionKey === 'loanCategory'
            ).selectedOptions;
            let loanPurpose = displayOptions.find(
                (x) => x.optionKey === 'loanPurpose'
            ).selectedOptions;
            let loanStatus =
                displayOptions.find((x) => x.optionKey === 'activeMilestones')?.selectedOptions ??
                '';

            let parameters: IPipelineParameters = {
                fromDatePeriod: fromDatePeriod,
                channel: channel,
                loanType: loanType,
                loanPurpose: loanPurpose,
                hFilterJSON: hFilterJSON,
                priorCompareTo: priorCompareTo,
                milestone: loanStatus,
            };

            let params = Object.assign({}, parameters);
            params.milestoneKey = cardInfo.milestoneKey.toString();

            this._pipelineServices.getSmallCardData(params).subscribe(
                (data) => {
                    return this.setPipelineSmallCardData(data, cardInfo, action, routerLink);
                },
                (err) => {
                    return this.setPipelineSmallCardData(
                        [],
                        cardInfo,
                        NoDataActionType.error,
                        routerLink
                    );
                }
            );
        }
    }

    populateProductionSmallCardData(
        milestoneCard: ICardInfo,
        data,
        action: NoDataActionType,
        params: IPipelineParameters
    ) {
        let thisPeriodData: ProductionSmallCard = data.find(
            (item) => item.periodGroup === 'This Period'
        );
        let comparePeriodData: ProductionSmallCard = data.find(
            (item) => item.periodGroup === 'Compare Period'
        );
        let isComparable = false;
        let comparisonPercentage = 0;
        if (thisPeriodData !== null && thisPeriodData) {
            let comparisonInf = this.reusableFunctions.calculateComparisonPercentage(
                thisPeriodData.kpiValue == null ? 0 : thisPeriodData.kpiValue,
                comparePeriodData.kpiValue == null ? 0 : comparePeriodData.kpiValue
            );

            isComparable = comparisonInf.isComparable;
            comparisonPercentage = comparisonInf.percentage;
        }

        let isInverse = false;
        if (milestoneCard.cardId == 'appToFund') {
            isInverse = true;
        }
        const compareVal = this.reusableFunctions.getCaret(
            comparisonPercentage,
            isInverse,
            null,
            isComparable
        );
        const mainVal =
            thisPeriodData == null
                ? '0'
                : (thisPeriodData.kpiValue == null ? 0 : thisPeriodData.kpiValue).toString();
        this.setProductionSmallCard(
            milestoneCard,
            mainVal,
            compareVal,
            data.length > 0,
            action,
            params
        );
    }

    populateProductionSmallCardMilestoneAvgData(
        cardInfo: ICardInfo,
        data,
        action: NoDataActionType,
        params: IPipelineParameters
    ) {
        let thisPeriodData: ProductionSmallCardMilestoneAvg = data.find(
            (item) => item.periodGroup === 'This Period'
        );
        let comparePeriodData: ProductionSmallCardMilestoneAvg = data.find(
            (item) => item.periodGroup === 'Compare Period'
        );

        let isComparable = false;
        let comparisonPercentage = 0;
        if (thisPeriodData !== null) {
            let comparisonInf = this.reusableFunctions.calculateComparisonPercentage(
                !thisPeriodData ? 0 : thisPeriodData.avg_nbr,
                !comparePeriodData ? 0 : comparePeriodData.avg_nbr
            );

            isComparable = comparisonInf.isComparable;
            comparisonPercentage = comparisonInf.percentage;
        }

        const compareVal = this.reusableFunctions.getCaret(
            comparisonPercentage,
            false,
            null,
            isComparable
        );
        const mainVal =
            thisPeriodData && thisPeriodData.avg_nbr ? thisPeriodData.avg_nbr.toFixed(2) : '0';
        this.setProductionSmallCard(cardInfo, mainVal, compareVal, data.length > 0, action, params);
    }

    setProductionSmallCard(
        cardInfo: ICardInfo,
        mainValue,
        compareVal,
        hasData: boolean,
        action: NoDataActionType,
        params: IPipelineParameters
    ) {
        let cardData = cardInfo.card;
        let queryParams = this.getQueryParams(cardData.urlSegment, params);
        cardData.mainValue = this.formatMainValueFromSmallCardTitle(mainValue, cardInfo);
        cardData.svgData = compareVal.svgData;
        cardData.faIcon = compareVal.ng_caret_icon;
        cardData.faIconColor = compareVal.color;
        cardData.comparisonValue = compareVal.comparison;
        cardData.isDataReady = true;
        cardData.hasData = hasData;
        cardData.queryParams = queryParams;
        cardData.showCardControllers = true;
        cardData.action = action;
        cardInfo.card = Object.assign({}, cardData);
    }

    prepareProductionPage(
        displayOptions: DisplayOptions[],
        hFilterJSON: string,
        cardInfo: ICardInfo,
        action: NoDataActionType
    ) {
        let fromDatePeriod = displayOptions.find(
            (item) => item.optionKey === 'dateRange_y_q_m'
        ).selectedOptions;
        let priorCompareToInfo = displayOptions.find((item) => item.optionKey === 'compareTo');
        let priorCompareTo = priorCompareToInfo.selectedOptions;
        let channel = displayOptions.find((x) => x.optionKey === 'channel').selectedOptions;
        let loanType = displayOptions.find((x) => x.optionKey === 'loanCategory').selectedOptions;
        let loanPurpose = displayOptions.find((x) => x.optionKey === 'loanPurpose').selectedOptions;
        let milestone =
            displayOptions.find((x) => x.optionKey === 'activeMilestones')?.selectedOptions ??
            'All';

        let parameters: IPipelineParameters = {
            fromDatePeriod: fromDatePeriod,
            channel: channel,
            loanType: loanType,
            loanPurpose: loanPurpose,
            hFilterJSON: hFilterJSON,
            priorCompareTo: priorCompareTo,
            milestone: milestone,
        };

        let params = Object.assign({}, parameters);
        params.milestoneKey = cardInfo.milestoneKey.toString();
        if (cardInfo.milestoneKey != 22) {
            this._productionService
                .displaySmallKPICardValue(
                    fromDatePeriod,
                    priorCompareTo,
                    cardInfo.milestoneKey,
                    channel,
                    loanType,
                    loanPurpose,
                    hFilterJSON
                )
                .subscribe(
                    (res) => {
                        this.populateProductionSmallCardData(cardInfo, res, action, params);
                    },
                    (error) => {
                        this.populateProductionSmallCardData(
                            cardInfo,
                            [],
                            NoDataActionType.error,
                            params
                        );
                    }
                );
        } else {
            this._productionService
                .displaySmallMilestoneAvgCardValue(
                    fromDatePeriod,
                    priorCompareTo,
                    cardInfo.milestoneKey,
                    cardInfo.avgType,
                    channel,
                    loanType,
                    loanPurpose,
                    hFilterJSON
                )
                .subscribe(
                    (res) => {
                        this.populateProductionSmallCardMilestoneAvgData(
                            cardInfo,
                            res,
                            action,
                            params
                        );
                    },
                    (error) => {
                        this.populateProductionSmallCardMilestoneAvgData(
                            cardInfo,
                            [],
                            NoDataActionType.error,
                            params
                        );
                    }
                );
        }
    }

    preparePerformancePage(
        displayOptions: DisplayOptions[],
        hFilterJSON: string,
        cardInfo: ICardInfo,
        action: NoDataActionType
    ) {
        const fromDatePeriod =
            displayOptions.find((item) => item.optionKey === 'dateRangeYtd')?.selectedOptions ??
            'This Year';

        if (cardInfo.isHeadcount) {
            this.performanceService
                .getPerformanceHeadCountTotals('Last Month', cardInfo.type, hFilterJSON)
                .subscribe(
                    (data: IHeadCountTotals[]) => {
                        this.handlePerformanceHeadcountSmallcards(data, cardInfo, 0, action);
                    },
                    () => {
                        cardInfo.card.action = NoDataActionType.error;
                        this.handlePerformanceHeadcountSmallcards(
                            [],
                            cardInfo,
                            0,
                            NoDataActionType.error
                        );
                    }
                );
        } else {
            let tenuredOnly = true;

            if (cardInfo.type === 'Score') {
                tenuredOnly = false;
            }

            this.performanceService
                .getScoreCardGrades(
                    fromDatePeriod,
                    cardInfo.type,
                    cardInfo.range,
                    cardInfo.rangeType,
                    cardInfo.rangeLimit,
                    tenuredOnly,
                    hFilterJSON
                )
                .subscribe(
                    (data: IScoreCardGrades[]) => {
                        //TODO: get url segment wth main data instead of this calling.
                        this.positionService.getScorecardPositions().subscribe((res) => {
                            let posAbbv = data[0]?.positionAbbv?.toLowerCase() ?? '';
                            let urlSegmentinf = res.find(
                                (r) => r.positionAbbv.toLowerCase() === posAbbv
                            );
                            cardInfo.urlSegment = urlSegmentinf?.scoreCardUrlSegment ?? 'lo';
                            this.handlePerformanceRankingSmallCards(data, cardInfo, 0, action);
                        });
                    },
                    () => {
                        cardInfo.card.action = NoDataActionType.error;
                        this.handlePerformanceRankingSmallCards(
                            [],
                            cardInfo,
                            0,
                            NoDataActionType.error
                        );
                    }
                );
        }
    }

    handlePerformanceRankingSmallCards(
        data: IScoreCardGrades[],
        cardData: any,
        index: number,
        action: NoDataActionType
    ) {
        let hasData = false;
        let smallCard = cardData.card;

        if (data.length > 0) {
            hasData = true;

            let tableRowItems: IRowItems[] = [];
            data.forEach((dataItem) => {
                dataItem.tenure = `${dataItem.tenureYears}Yr ${dataItem.tenureMonths}Mo`;
                let splitedName = dataItem.employee.split(',');
                dataItem.shortName = splitedName[0] + ', ' + splitedName[1].trimLeft().slice(0, 1);

                let tableTdItems = [];
                if (cardData.cardDataRowinfo) {
                    cardData.cardDataRowinfo.forEach((rowInf) => {
                        let rowInfCopy = Object.assign({}, rowInf);

                        if (rowInfCopy.headerTitle === 'Score') {
                            rowInfCopy.value = dataItem[rowInfCopy.fieldId].toFixed(
                                rowInfCopy.precision
                            );
                        } else if (rowInfCopy.headerTitle === 'Units') {
                            rowInfCopy.value = dataItem[rowInfCopy.fieldId].toLocaleString();
                        } else if (rowInfCopy.headerTitle === 'Volume') {
                            rowInfCopy.value = this.reusableFunctions.formatMoney(
                                dataItem[rowInfCopy.fieldId]
                            );
                        } else {
                            rowInfCopy.value = dataItem[rowInfCopy.fieldId]
                                ? dataItem[rowInfCopy.fieldId].toString()
                                : '';
                        }

                        if (rowInfCopy.isHyperlinked) {
                            rowInfCopy.routerLink = '/performance/scorecards';
                            rowInfCopy.urlSegment = cardData.urlSegment;
                            rowInfCopy.queryParams = {
                                id: dataItem[rowInfCopy.hyperlinkFieldId],
                            };

                            rowInfCopy.hasAccess = this.userAccessService.hasAccessToPage(
                                rowInfCopy.routerLink + '/' + rowInfCopy.urlSegment
                            );
                        }
                        tableTdItems.push(rowInfCopy);
                    });

                    tableRowItems.push({
                        tdList: tableTdItems,
                    });
                }
            });
            smallCard.tableData = tableRowItems;
        }

        smallCard.isDataReady = true;
        smallCard.hasData = hasData;
        smallCard.action = action;
        smallCard.searchTerm = '';
        smallCard.styleSet = { 'card-container-area': { height: '235px' } };
        smallCard.showCardControllers = true;
        cardData.card = Object.assign({}, smallCard);
    }

    handlePerformanceHeadcountSmallcards(
        data: IHeadCountTotals[],
        cardData: any,
        index: number,
        action: NoDataActionType
    ) {
        let hasData = false;
        let smallCard = cardData.card;

        if (data.length === 2) {
            let thisPeriodData = data.find((item) => item.periodGroup === 'This Period');
            let lastPeriodData = data.find((item) => item.periodGroup === 'Compare Period');
            if (thisPeriodData.totalCount != null && lastPeriodData.totalCount != null) {
                hasData = true;
                this.getPerformanceHeadcountsSmallCardGenericData(
                    smallCard,
                    thisPeriodData.totalCount,
                    lastPeriodData.totalCount
                );
            }
            if (thisPeriodData.tenureCount != null && thisPeriodData.nonTenureCount != null) {
                const hasAccess = this.userAccessService.hasAccessToPage(
                    cardData.cardDrilldownUrl + '/' + smallCard.drilldownSegment
                );
                smallCard.bottomCardData = [
                    {
                        value: thisPeriodData.tenureCount.toString(),
                        description: 'Tenured',
                        isHyperlinked: true,
                        routerLink: cardData.cardDrilldownUrl,
                        urlSegment: smallCard.drilldownSegment,
                        queryParams: {
                            tenure: 'Tenured',
                            titleSuffix: ': Tenured',
                        },
                        hasAccess,
                    },
                    {
                        value: thisPeriodData.nonTenureCount.toString(),
                        description: 'Nontenured',
                        isHyperlinked: true,
                        routerLink: cardData.cardDrilldownUrl,
                        urlSegment: smallCard.drilldownSegment,
                        queryParams: {
                            tenure: 'Nontenured',
                            titleSuffix: ': Nontenured',
                        },
                        hasAccess,
                    },
                ];
            }
        }

        smallCard.routerLink = cardData.cardDrilldownUrl;
        smallCard.isDataReady = true;
        smallCard.queryParams = { tenure: 'All', titleSuffix: '' };
        smallCard.hasData = hasData;
        smallCard.action = action;
        smallCard.searchTerm = '';
        smallCard.showCardControllers = true;
        //Below is a really hacky solution to force change detection in child components
        cardData.card = Object.assign({}, smallCard);
    }

    getPerformanceHeadcountsSmallCardGenericData(
        card: SmallCardData,
        mainValue: number,
        lastPeriodValue: number,
        unit: string = null,
        toFixed: number = 0
    ) {
        //let comparisonValue = null;
        if (mainValue != null && lastPeriodValue != null) {
            let comparisonInf = this.reusableFunctions.calculateComparisonPercentage(
                mainValue,
                lastPeriodValue
            );
            let caretInfo = this.reusableFunctions.getCaret(
                comparisonInf.percentage,
                false,
                null,
                comparisonInf.isComparable
            );
            card.faIcon = caretInfo.ng_caret_icon;
            card.faIconColor = caretInfo.color;
            if (lastPeriodValue != 0) {
                card.comparisonValue = caretInfo.comparison;
            }
            card.svgData = caretInfo.svgData;
            card.unit = unit ? unit : null;
            card.mainValue = mainValue.toFixed(toFixed);
            card.styleSet = { 'card-container-area': { height: '235px' } };
        }
    }

    getQueryParams(urlSegment: string, params: IPipelineParameters) {
        let queryParams: object = { compareTo: params.priorCompareTo };
        if (urlSegment === 'purchases') {
            queryParams['purpose'] =
                params.loanPurpose.includes('Purchase') || params.loanPurpose === 'All'
                    ? 'purchase'
                    : '';
        } else if (urlSegment === 'government') {
            queryParams['loanType'] =
                params.loanType.includes('Govt') || params.loanType === 'All' ? 'govt' : '';
        }
        return queryParams;
    }

    formatMainValueFromSmallCardTitle(mainValue: number, cardInfo: ICardInfo): string {
        if (cardInfo.dataType && cardInfo.dataType === 'percent') {
            return mainValue.toString() + '%';
        } else {
            return mainValue.toString();
        }
    }
}
